import {watch, nextTick, onScopeDispose, type WatchSource} from 'vue';

/**
 * Watches a reactive source and executes a handler after the DOM updates
 * and the browser's History API fully synchronizes window.location.
 * Automatically cleans up pending timeouts when the component is unmounted.
 *
 * @param source - The reactive source to watch (e.g., a getter function or ref).
 * @param handler - The function to execute with the new value once everything is fully ready.
 */
export function useDeferredWatch<T>(
	source: WatchSource<T>,
	handler: (value: NonNullable<T>) => void,
): void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	let disposed = false;

	watch(
		source,
		async (newValue) => {
			if (!newValue) {
				return;
			}

			await nextTick();

			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			// Push to the macro-task queue to guarantee window.location is fully synchronized by the History API
			timeoutId = setTimeout(() => {
				if (disposed) {
					return;
				}

				handler(newValue as NonNullable<T>);
			}, 0);
		},
		{immediate: true},
	);

	// Ensure no memory leaks or unexpected executions if the component is unmounted mid-flight
	onScopeDispose(() => {
		disposed = true;

		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	});
}
